<template>
  <div class="full-height" v-hotkey="keymap">
  <text-and-tools-panel 
    ref="textAndTools"
    :text-panel-styles="textPanelStyles"
    :tokens="localTokensObj.array"
    :auto-navigation-obj="autoNavigationObj"
    @token-selection="tokenSelected"
    @range-selection="rangeSelected"
    @saving-and-completing="(statusObj) => $emit('saving-and-completing', statusObj)"
    :non-confident-marking="nonConfidentMarking"
    :letter-adding-mode="modesObj.letterAddingMode"
    @update-letter-adding-mode="(newLetterAddingMode) => modesObj.letterAddingMode = newLetterAddingMode"
    :allow-showing-hideables="true"
    :hideables-name="$settings.hebrew ? 'ראשי תיבות' : 'Abbreviations'"
    :show-hideables="$store.state.postSubmissionOptions.showAims"
    @change-show-hideables="(newShowHideables) => setShowAims(newShowHideables)"
    :hideables-export-settings="{ USER_CONTROLLED: true, FOLLOW_DISPLAY: false }"
    :download-types="downloadTypes"
    :original-document-source="$store.state.originalDocument.source"
    @download="actuallyDownload"
    :can-undo="canUndo"
    :can-redo="canRedo"
    @undo="undoOneChange"
    @redo="redoOneChange"
    @open-free-edit-popup="openEditNikudPopup"
    :show-saved-state-message="showSavedStateMessage"
    :disable-back-to-text-prep-prompt="$store.state.postSubmissionOptions.disableBackToTextPrepPrompt"
    @back-to-text-prep="actuallyBackToTextPrep"
    :text-panel-ref="$refs.textPanel"
  >
    <template v-slot:textpanel="props">
      <text-panel
          ref="textPanel"
          :tokens="localTokensObj.array"
          :style="props.fstyle"
          class="main-results-box"
          @token-selection="tokenSelected"
          @range-selection="rangeSelected"
          :announce-all-navigation="true"
      ></text-panel>
    </template>
    <edit-nikud-pane v-if="editNikudProps.showEditNikud" :edit-nikud-props="editNikudProps" :modes-obj="modesObj" />
  </text-and-tools-panel>
  </div>
</template>

<script>
// @ is an alias to /src

import nikudMixins from '@/mixins/nikudMixins'
import UndoRedoMixins from 'shared/UndoRedo/UndoRedoMixins'
import {undoRedoState} from 'shared/UndoRedo'
import EditNikudPane from './EditNikudPane'
import TextAndToolsPanel from 'shared/components/TextAndToolsPanel/TextAndToolsPanel'
import serviceMixins from '@/mixins/serviceMixins'
import navigationMixins from '@/mixins/navigationMixins'
import {AppConfig} from '../appConfig'
import styleGlobals from 'shared/js/forTextPanel/styleGlobals'
import {generateWordDoc} from '@/js/wordExport'
import {processSpecialDisplayProps} from 'shared/js/processSpecialDisplayProps'
import conditionalOptions from 'shared/js/conditionalOptions'
import {METEG} from 'shared/js/commonHebrew'
import {registerContextCallback, unregisterContextCallback} from 'shared/UndoRedo'
import {getStandardLetterArray} from '@/js/letterOperations'
import {getTextPanel} from 'shared/components/NewTextPanel/TextPanel'
import _ from 'lodash'
import $ from 'jquery'

const spaceSubstituteRegex = new RegExp(styleGlobals.SPACE_SUBSTITUTE, 'g')
let vm = null
let oldWordsArrayLength = 0
let unwatchers = []
let lastTokenSelection = null

const TextPanel = getTextPanel({
  getDisplay (t) { return {
    display: t.display ? t.display : t.text,
    classes: t.classes
  } }
})

function undoRedoUpdateContext(contextData) {
  vm.$store.commit('SET_WORD_AND_LETTER',
    vm.getSafeWordAndLetter(contextData.wordIndex, contextData.letterIndex))
}

export default {
  name: 'NikudResultsContainer',
  mixins: [
    nikudMixins, /* for the direct use of: localLetterAddingMode, localCurrentWordIndex, localCurrentLetterIndex,
                      localCurrentWord, modalOpen(), display(),
                    ...and for the use, as an undo()-and-redo() dependency, of: getSafeWordAndLetter() */
    UndoRedoMixins, // for undo(), redo(), canUndo, & canRedo
    serviceMixins, // for gtag()
    navigationMixins // for moveToWordMethod()
  ],
  props: ['modesObj', 'editNikudProps', 'showSavedStateMessage'],
  components: {
    TextAndToolsPanel,
    EditNikudPane,
    TextPanel
  },
  data () {
    return {
      autoNavigationObj: {
        command: null,
        arguments: { tokenIndex: null, letterIndex: null }
      },
      currentSelection: [],
      localTokensObj: {}, // holds the tokens converted from the wordsArray from the store for the TextPanel
      downloadTypes: ['text'] // chosen from: 'docx', 'text', 'html' (sync with toolbar!)
    }
  },
  computed: {
    textPanelStyles () {
      return {
        Mobile: window.innerWidth < 992,
        Desktop: window.innerWidth >= 992,
        textPanel: {
          USE_LETTER_CURSOR: false,
          SHOW_LINE_BREAK_CHAR: true,
          CAN_CLICK_ON_LETTERS: false,
          START_BY_SINGLE_CLICK: false,
          START_BY_DOUBLE_CLICK: false
        },
        appFeatures: {
          showPasukFont: AppConfig.SHOW_PASUK_FONT,
          hideablesSuffixesDuplicated: false,
          implementQamatzQatan: false,
          editNikudPopup: {
            ENABLE: true,
            PRESENT: true
          }
        },
        tools: {
          BOTTOM: true,
          HEADER: false,
          USE_DOWNLOAD_POPUP: true
        }
      }
    },
    nonConfidentMarking () {
      return AppConfig.NON_CONFIDENT_MARKING
    },
    localWordsArray () {
      return this.$store.state.allWorkingNikud.wordsArray
    },
    localShowAims () {
      return this.$store.state.postSubmissionOptions.showAims
    },
    keymap () {
      return {
        'shift+r': () => { if (!(this.modalOpen())) this.dictionaryEntryFromRange() },
        'shift+w': () => { if (!(this.modalOpen())) this.dictionaryEntryFromRange() },
        'shift+e': () => { if (!(this.modalOpen())) this.openEditNikudPopup() }
      }
    }
  },
  watch: {
    localCurrentWordIndex (newWordIndex) {
      this.autoNavigate(newWordIndex, this.localCurrentLetterIndex)
    },
    localCurrentLetterIndex (newLetterIndex) {
      this.autoNavigate(this.localCurrentWordIndex, newLetterIndex)
    },
    localShowAims (newLocalShowAims) {
      if (newLocalShowAims) {
        this.gtag("event", "change display", {
          'event_category': 'all word displays',
          'event_label': 'show abbreviations'
        })
      }
    },
    localWordsArray: {
      handler (n, o) {
        if (n === o && n.length === oldWordsArrayLength) return
        oldWordsArrayLength = n.length
        var tokensArray = []
        var tokensToWordObjParts = []
        var wordObjPartsToTokensMaps = []
        for (let unwatch of unwatchers) {
          unwatch()
        }
        this.localWordsArray.forEach((_, wordIndex) => {
          let tokensArrayOffset = tokensArray.length
          const tokensData = this.getTokensFromWord(wordIndex, tokensArray.length)
          tokensArray.push(...tokensData.tokensArray)
          tokensToWordObjParts.push(...tokensData.tokensToWordObjParts)
          wordObjPartsToTokensMaps.push(tokensData.wordObjPartsToTokensMap)
          const unwatch = this.$watch(
            /* $watch() arg #1 - fn to watch: */ () => {
              if (wordIndex > 0 && this.localTokensObj.array?.length) { // may need to correct tokensArrayOffset
                const tokensOfPreviousWord = wordObjPartsToTokensMaps[wordIndex - 1]
                const previousTokenIndex = tokensOfPreviousWord.sepAfter
                                            ? tokensOfPreviousWord.sepAfter : tokensOfPreviousWord.word
                tokensArrayOffset = previousTokenIndex + 1
              }
              return this.getTokensFromWord(wordIndex, tokensArrayOffset)
            },
            /* $watch() arg #2 - callback: */ newVal => {
              newVal.tokensArray.forEach((token, i) => {
                const tokenIndex = tokensArrayOffset + i
                const tokenToWordObjPartsData = newVal.tokensToWordObjParts[i]
                if (tokenIndex === this.localTokensObj.array.length) { // i.e. NEW token at END
                  this.localTokensObj.array.push(token)
                  this.localTokensObj.mapToWordObjParts.push(tokenToWordObjPartsData)
                } else {
                  let wordIndexTheTokenIndexNowMapsTo = this.localTokensObj.mapToWordObjParts[tokenIndex].wordIndex
                  if (wordIndexTheTokenIndexNowMapsTo === wordIndex) {
                    Object.assign(this.localTokensObj.array[tokenIndex], token)
                    Object.assign(this.localTokensObj.mapToWordObjParts[tokenIndex], tokenToWordObjPartsData)
                  } else { // i.e. ADDITIONAL token in this word - so old map points to NEXT word - fix both:
                    this.localTokensObj.array.splice(tokenIndex, 0, token)
                    this.localTokensObj.mapToWordObjParts.splice(tokenIndex, 0, tokenToWordObjPartsData)
                  }
                }
                if (i === (newVal.tokensArray.length - 1) && wordIndex === (this.localWordsArray.length - 1)) {
                  const oldTokensAfter = (this.localTokensObj.array.length - 1) - tokenIndex
                  if (oldTokensAfter) {
                    this.localTokensObj.array.splice(tokenIndex + 1, oldTokensAfter)
                    this.localTokensObj.mapToWordObjParts.splice(tokenIndex + 1, oldTokensAfter)
                  }
                }
              })
              Object.assign(this.localTokensObj.wordObjPartsToTokensMaps[wordIndex], newVal.wordObjPartsToTokensMap)
            }
          )
          unwatchers.push(unwatch)
        })
        this.localTokensObj = {
          array: tokensArray,
          mapToWordObjParts: tokensToWordObjParts,
          wordObjPartsToTokensMaps: wordObjPartsToTokensMaps
        }
      },
      immediate: true
    }
  },
  methods: {
    autoNavigate (wordIndex, letterIndex) {
      const wordObj = this.localWordsArray[wordIndex]
      const letterArrayLength = wordObj.standardLetterArray.length
      const partType = letterIndex < wordObj.sepBefore.length ? 'sepBefore'
                        : (letterIndex < wordObj.sepBefore.length + letterArrayLength ? 'word'
                            : 'sepAfter')
      const wordObjPartsToTokensMap = this.localTokensObj.wordObjPartsToTokensMaps[wordIndex]
      // this may not be set, e.g. on reload; after one "recurse" it will be
      if (!wordObjPartsToTokensMap) { setTimeout(() => { this.autoNavigate(wordIndex, letterIndex) }, 1); return }
      const args = this.autoNavigationObj.arguments
      args.tokenIndex =  wordObjPartsToTokensMap[partType]
      var letterIndexInToken = letterIndex
      if (partType !== 'sepBefore') {
        letterIndexInToken -= wordObj.sepBefore.length
        if (partType !== 'word') letterIndexInToken -= letterArrayLength
      }
      if (partType === 'word' && wordObj.wasAnAbbreviation && !this.localShowAims) {
        // fix up the issue with invisible aimot adding to the word length
        for (let letter of wordObj.standardLetterArray.slice(0, letterIndexInToken)) {
          if (letter.includes(METEG)) { letterIndexInToken-- }
        }
      }
      args.letterIndex = letterIndexInToken
      this.autoNavigationObj.command = 'move'
      if (!lastTokenSelection ||
          lastTokenSelection.tokenIndex !== args.tokenIndex ||
          lastTokenSelection.letterIndex !== args.letterIndex) {
        this.$refs.textPanel.navigate(this.autoNavigationObj)
      }
    },
    dictionaryEntryFromRange () {
      if (!(AppConfig.OFFER_CONDITIONAL_OPTIONS)) return
      const sorted = this.currentSelection.slice().sort((a, b) => a - b) // just in case
      if (sorted.length > 1 && sorted[0] === this.localCurrentWordIndex) {
        const wordString = this.localCurrentWord.chosenWordString
        const componentsArray = [wordString].concat(
          this.currentSelection.slice(1).map(i => this.localWordsArray[i].originalWord))
        const dictString = conditionalOptions.dictStringFromAfterWordComponentsArray(componentsArray)
        this.$store.commit('UPDATE_USER_DICTIONARY', {
          contextData: { wordIndex: this.localCurrentWordIndex, letterIndex: this.localCurrentLetterIndex }, // for undo
          wordsToAdd: [dictString],
          toChangeWhetherPinned: { dictString: dictString, newValueOfWhetherPinned: 'pinned'}
        })
      } // else console.log('no multi-word selection, starting with current word, on record!')
    },
    tokenSelected (tokenSelectionObj) {
      lastTokenSelection = tokenSelectionObj
      const wordObjPartFromToken = this.localTokensObj.mapToWordObjParts[tokenSelectionObj.tokenIndex]
      const forward = (wordObjPartFromToken.wordIndex > this.localCurrentWordIndex) &&
                      tokenSelectionObj.trigger !== 'click'
      if (tokenSelectionObj.letterIndex === 0 && forward && wordObjPartFromToken.partType === 'sepBefore') {
        this.changeCurrentWordIndex(wordObjPartFromToken.wordIndex)
      } else {
        var letterIndexInWord = tokenSelectionObj.letterIndex || 0
        if (wordObjPartFromToken.partType !== 'sepBefore') {
          const wordObj = this.localWordsArray[wordObjPartFromToken.wordIndex]
          letterIndexInWord += wordObj.sepBefore.length
          if (wordObjPartFromToken.partType !== 'word') {
            letterIndexInWord += wordObj.standardLetterArray.length
          } else if (wordObj.wasAnAbbreviation && !this.localShowAims) {
            // fix up the issue with invisible aimot adding to the word length
            let counter = tokenSelectionObj.letterIndex
            for (let letter of wordObj.standardLetterArray ) {
              if (letter.includes(METEG)) {
                letterIndexInWord++
              } else {
                if (counter === 0) break
                counter--
              }
            }
          }
        }
        if (wordObjPartFromToken.wordIndex !== this.localCurrentWordIndex
            || letterIndexInWord !== this.localCurrentLetterIndex) this.changeWordAndLetter({
          word: wordObjPartFromToken.wordIndex,
          letter: letterIndexInWord
        })
      }
    },
    rangeSelected (rangeSelection) {
      const tokenSelectionRange = rangeSelection.cleared ? [] : _.range(rangeSelection.from, rangeSelection.to + 1)
      // FIXME: there's no way this is a reasonable way to do this
      this.$refs.textAndTools.currentSelection = tokenSelectionRange
      var wordSelectionRange = []
      tokenSelectionRange.forEach(tokenIndex => {
        const wordIndex = this.localTokensObj.mapToWordObjParts[tokenIndex].wordIndex
        if (!(wordSelectionRange.includes(wordIndex))) wordSelectionRange.push(wordIndex)
      })
      this.currentSelection = wordSelectionRange
    },
    openEditNikudPopup () {
      this.editNikudProps.workingIndices = this.currentSelection.length ? this.currentSelection : [this.localCurrentWordIndex]
      // console.log('this.editNikudProps.workingIndices:')
      // console.log(this.editNikudProps.workingIndices)
      this.modesObj.letterAddingMode = false
      this.editNikudProps.showEditNikud = true
      setTimeout(() => { $('.nikud-manual-edit-area').focus() }, 10)
    },
    changeCurrentWordIndex (w) {
      this.moveToWordMethod(w)
    },
    changeCurrentLetterIndex (le) { // currently never used here! (a COPY in ManualNikudPane is used a lot)
      this.$store.commit('SET_CURRENT_LETTER_INDEX', le)
    },
    changeWordAndLetter (indicesObj) {
      this.$store.commit('SET_WORD_AND_LETTER', indicesObj)
    },
    setShowAims (a) {
      // console.log('committing SET_SHOW_AIMS - setting to ' + a)
      this.$store.commit('SET_SHOW_AIMS', a)
    },
    undoOneChange () {
      if (this.canUndo) {
        const allPostSubmissionOptions = Object.assign({}, this.$store.state.postSubmissionOptions)
        this.undo()
        this.addWordMutationWhenUndoRedoDone()
        this.$store.commit('SET_ALL_POST_SUBMISSION_OPTIONS', allPostSubmissionOptions)
      }
      document.activeElement.blur()
    },
    redoOneChange () {
      if (this.canRedo) {
        this.redo()
        this.addWordMutationWhenUndoRedoDone()
      }
      document.activeElement.blur()
    },
    addWordMutationWhenUndoRedoDone () {
      if (undoRedoState.undoRedoExecuting) {
        const intervalHolder = setInterval(() => {
          if (!(undoRedoState.undoRedoExecuting)) {
            this.modesObj.wordMutationCount += 1
            clearInterval(intervalHolder)
          }
        }, 5)
      } else this.modesObj.wordMutationCount += 1
    },
    actuallyBackToTextPrep (disablePrompt) {
      this.$emit('back-to-text-prep-confirmed')
      if (disablePrompt) this.$store.commit('SET_DISABLE_BACK_TO_TEXT_PREP_PROMPT', true)
      this.$store.commit('EDIT_DOCUMENT_TEXT', {text: '', nullifySource: true})
      this.$router.push({ name: 'text-prep' })
    },
    actuallyDownload (downloadOptions) {
      const downloadWithAims = downloadOptions.downloadWithHideables
      const HSD = true // i.e. hideablesSuffixesDuplicated, ALWAYS false for the abbreviation expander
      var text = /* by default */ processSpecialDisplayProps(this.localTokensObj.array, downloadWithAims, HSD)
      // console.log(text)
      var element
      if (downloadOptions.fileTypeName === 'text') {
        element = document.createElement('a')
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
        element.setAttribute('download', 'AbbreviationResults' + Math.floor(Math.random() * 100000000) + '.txt')
        element.style.display = 'none'
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
      } else if (downloadOptions.fileTypeName === 'html') {
        text = '<body dir="rtl" style="font-size: ' + styleGlobals.DEFAULT_FONT_SIZE + 'px">' + this.localWordsArray.map(
          (wordObj) => {
            const htmlIfNotCurrent = this.getHtmlIfNotCurrent(wordObj)
            return htmlIfNotCurrent.before + '<span options="'
                + JSON.stringify(wordObj.options).replace(/"/g, '&quot;')
                + '" chosen-option-index="' + wordObj.chosenOptionIndex
                + '" original-word="' + wordObj.originalWord
                + '" manual-nikud="' + wordObj.manualNikud
                + '" edited-nikud="' + wordObj.editedNikud
                + '" choice-type="' + wordObj.choiceType + '"'
                + (wordObj.altered ? ' style="color: #bd10e0"' : '') + '>'
                + (downloadWithAims ? htmlIfNotCurrent.wordShowingAims 
                    : htmlIfNotCurrent.wordNotShowingAims) + '</span>' + htmlIfNotCurrent.after
          }).join('') + '</body>'
        element = document.createElement('a')
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
        element.setAttribute('download', 'AbbreviationResults' + Math.floor(Math.random() * 100000000) + '.html')
        element.style.display = 'none'
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)        
      } else { /* downloadOptions.fileTypeName === 'docx' */
        var base64 = generateWordDoc(
          this.$store.state, { keepFormattingOption: downloadWithAims.keepFormattingOption,
                                          aimOption: downloadOptions.downloadWithHideables,
                                        appInHebrew: this.$settings.hebrew })
        // console.log(base64)
        element = document.createElement('a')
        element.setAttribute('href', 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + base64) // base64js.fromByteArray(contents))
        element.setAttribute('download', 'AbbreviationResults' + Math.floor(Math.random() * 100000000) + '.docx')
        element.style.display = 'none'
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
      }
    },
    getWordHtml (wordObj, letterArray, aimCondition = this.$store.state.postSubmissionOptions.showAims) {
      const CHOLAM = 'ֹ'
      var wordHtml = ''
      var shiftedCholam = false
      var getShiftedCholam = false
      letterArray.forEach((letterString, i) => {
        let currentLetterString = letterString
        wordHtml += '<span id="span-L-word-' + wordObj.wordId + '-letter-' + (i + wordObj.sepBefore.length) + '"'
        var styles = []
        if (letterString.includes('ֽ')) {
          letterString = aimCondition ? letterString[0] : ''
          currentLetterString = aimCondition ? letterString[0] : ''
          styles.push('color: ' + styleGlobals.hideableColor)
        } else if (aimCondition && letterArray[i + 1] === 'וֽ' && letterString[letterString.length - 1] === CHOLAM) {
          currentLetterString = letterString.slice(0, letterString.length - 1)
          shiftedCholam = true
        }
        if (styles.length) wordHtml += ' style="' + styles.join('; ') + '"'
        wordHtml += '>' + currentLetterString + '</span>' + (getShiftedCholam ? CHOLAM : '')
        if (shiftedCholam) { shiftedCholam = false; getShiftedCholam = true } else getShiftedCholam = false
      })
      return wordHtml // (no need to replace line breaks - they're only in separators!) // this.display(wordHtml)
    },
    getSepHtml (wordObj, sepType, letterArray) {
      var wordHtml = ''
      const sepChars = (sepType === 'before' ? wordObj.sepBefore : wordObj.sepAfter)
      var indexDelta = 0 // for 'before'
      if (sepType === 'after') indexDelta += (letterArray.length + wordObj.sepBefore.length)
      sepChars.split('').forEach((letterString, i) => {
        wordHtml += '<span id="span-S-word-' + wordObj.wordId + '-letterpos-' + (i + indexDelta) + '"'
        wordHtml += '>' + letterString + '</span>'
      })
      return this.display(wordHtml)
    },
    getHtmlIfNotCurrent (wordObj) {
      const letterArray = wordObj.standardLetterArray
      return {
        before: this.getSepHtml(wordObj, 'before', letterArray),
        wordShowingAims: this.getWordHtml(wordObj, letterArray, true),
        wordNotShowingAims: this.getWordHtml(wordObj, letterArray, false),
        after: this.getSepHtml(wordObj, 'after', letterArray)
      }
    },
    getTokensFromWord(wordIndex, firstTokenIndex) {
      // we first look up the word object, because if the word is replaced (e.g. by edit), then there may be a new
      // object at this index
      const wordObj = this.localWordsArray[wordIndex]
      const tokensArray = []
      const tokensToWordObjParts = []
      const currentWordObjPartsToTokensMap = { sepBefore: null, sepAfter: null }
      if (wordObj.sepBefore) {
        tokensArray.push({
          isEditable: false, // i.e. arrows don't move to it
          altered: wordObj.altered, // not yet accurate! - but currently doesn't matter
          text: wordObj.sepBefore
        })
        tokensToWordObjParts.push({ wordIndex: wordIndex, partType: 'sepBefore' })
        currentWordObjPartsToTokensMap.sepBefore = firstTokenIndex + tokensArray.length - 1
      }
      const spaceToHideString = '>' + METEG + '\u00a0'
      const spaceToHideRegex = new RegExp(spaceToHideString, 'g')
      const postNikudShiftingString = wordObj.chosenWordString.replace(spaceSubstituteRegex, ' ') // no "nikud shifting" in abbreviation
                                                              .replace(spaceToHideRegex, spaceToHideString + METEG)
      const postNikudShiftingArray = getStandardLetterArray(postNikudShiftingString)
      const wordToken = {
        isEditable: wordObj.wasAnAbbreviation,
        altered: wordObj.altered, // not yet accurate! - but currently doesn't matter
        text: postNikudShiftingString,
        apiNonConfident: wordObj.specialData && wordObj.specialData.apiNonConfident,
        markedNonConfident: wordObj.specialData && wordObj.specialData.markedNonConfident,
        classes: []
      }
      if (wordObj.wasAnAbbreviation && postNikudShiftingString.includes(METEG)) {
        wordToken.text = []
        let currentPart = { part: '', hideable: postNikudShiftingArray[0].includes(METEG) }
        postNikudShiftingArray.forEach(letter => {
          if (letter.includes(METEG) !== currentPart.hideable) {
            currentPart.classes = [currentPart.hideable ? 'abbreviation' : 'expansion']
            wordToken.text.push(currentPart)
            currentPart = { part: letter.replace(/ֽ/g, ''), hideable: letter.includes(METEG) }
          } else currentPart.part += letter.replace(/ֽ/g, '')
        })
        currentPart.classes = [currentPart.hideable ? 'abbreviation' : 'expansion']
        if (currentPart.part) wordToken.text.push(currentPart)
        // The TextPanel doesn't deal with hideables. It expects invisible characters to be gone.
        // Unfortunately, these tokens are used for downloads and copying and updating the server, even though the wordsArray
        // hold the authoritative copy. Hopefully, it'll all move to tokens at some point, and this will make more sense.
        // We use a different property, `display`, to hold the version that the TextPanel needs.
        // The `getDisplay` function above will fall back to the `text` property if `display` isn't present.
        const textArrayForDisplay = this.localShowAims
                                      ? wordToken.text : wordToken.text.filter(part => !part.hideable)
        wordToken.display = textArrayForDisplay.map(part => ({
          text: part.part,
          classes: part.classes
        }))
      } else {
        wordToken.display = wordToken.text
      }
      if (this.nonConfidentMarking && wordToken.apiNonConfident) wordToken.classes.push('api-non-confident')
      if (this.nonConfidentMarking && wordToken.markedNonConfident) {
        wordToken.classes.push('marked-non-confident')
      }
      if (wordToken.altered) {
        wordToken.classes.push('altered-token')
      }
      tokensArray.push(wordToken)
      tokensToWordObjParts.push({ wordIndex: wordIndex, partType: 'word' })
      currentWordObjPartsToTokensMap['word'] = firstTokenIndex + tokensArray.length - 1
      if (wordObj.sepAfter) {
        tokensArray.push({
          isEditable: false, // i.e. arrows don't move to it
          altered: wordObj.altered, // not yet accurate! - but currently doesn't matter
          text: wordObj.sepAfter
        })
        tokensToWordObjParts.push({ wordIndex: wordIndex, partType: 'sepAfter' })
        currentWordObjPartsToTokensMap.sepAfter = firstTokenIndex + tokensArray.length - 1
      }
      return {
        tokensArray,
        tokensToWordObjParts,
        wordObjPartsToTokensMap: currentWordObjPartsToTokensMap
      }
    }
  },
  beforeDestroy () {
    unregisterContextCallback(undoRedoUpdateContext)
  },
  mounted () {
    const routeName = this.$route.name
    window.onkeydown = (e) => { 
      if (this.$route.name === routeName && !(this.modalOpen())) {
        var suppressKey = e.key === 'ArrowUp' || e.key === 'ArrowDown'
        if ($('.manual-input').is(':focus')) {
          if (e.shiftKey && (e.keyCode === 65 || e.keyCode === 81)) suppressKey = true // shift-A / shift-Q
        } else if (e.keyCode === 32 // space
                   || e.key === 'ArrowLeft' || e.key === 'ArrowRight') suppressKey = true
        return !(suppressKey) // suppress scrolling and hotkeys-in-textbox
      }
    }
    vm = this
    lastTokenSelection = null
    registerContextCallback(undoRedoUpdateContext)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.altered-token, .altered-token.api-non-confident {
  color: #bd10e0;
}
.abbreviation {
  color: #949494;
}
.expansion {
  color: #007EE5;
}
.api-non-confident {
  color: darkgray;
}
.marked-non-confident {
  border: solid red 1px;
}
.main-results-box, .main-results-box div {
  direction: rtl;
  text-align: right;
}
</style>
